import { useEffect, useState } from 'react';
import { Database, Wifi, Signal, Loader2, Gauge } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import PageHeader from '../../components/settings/PageHeader';
import SettingCard from '../../components/settings/SettingCard';
import Segmented from '../../components/settings/Segmented';
import Toggle from '../../components/settings/Toggle';
import { settingsService } from '../../services/settings/settings.service';
import type { DataUsageReport } from '../../types/settings.types';

function fmtMb(mb: number): string {
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb} MB`;
}

export default function DataUsage() {
  const [report, setReport] = useState<DataUsageReport | null>(null);
  const [dataSaver, setDataSaver] = useState(false);

  useEffect(() => {
    settingsService.getDataUsage().then((r) => {
      setReport(r);
      setDataSaver(r.dataSaver);
    });
  }, []);

  if (!report) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-400 dark:text-neutral-500">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const chartData = report.daily.map((d) => ({
    label: new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    Wifi: d.wifi,
    Cellular: d.cellular,
  }));

  return (
    <div className="space-y-6">
      <PageHeader title="Data usage" subtitle="Last 14 days across Wi-Fi and cellular" icon={Database} backTo="/app/settings/location" />

      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatTile icon={Gauge} label="Total" value={fmtMb(report.totalMb)} tone="bg-neutral-900 text-white" />
        <StatTile icon={Wifi} label="Wi-Fi" value={fmtMb(report.wifiMb)} tone="bg-white dark:bg-neutral-900" />
        <StatTile icon={Signal} label="Cellular" value={fmtMb(report.cellularMb)} tone="bg-white dark:bg-neutral-900" />
      </div>

      {/* Trend chart */}
      <SettingCard title="Daily usage">
        <div className="h-64 -ml-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="wifi" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#171717" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#171717" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="cell" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#a3a3a3" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#a3a3a3" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#a3a3a3' }} tickLine={false} axisLine={false} interval={1} />
              <YAxis tick={{ fontSize: 11, fill: '#a3a3a3' }} tickLine={false} axisLine={false} width={40} unit="M" />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: '1px solid #e5e5e5', fontSize: 12 }}
                formatter={(value) => `${value} MB`}
              />
              <Area type="monotone" dataKey="Wifi" stroke="#171717" strokeWidth={2} fill="url(#wifi)" />
              <Area type="monotone" dataKey="Cellular" stroke="#a3a3a3" strokeWidth={2} fill="url(#cell)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </SettingCard>

      {/* Breakdown */}
      <SettingCard title="By feature">
        <div className="space-y-3">
          {report.breakdown.map((b) => {
            const pct = Math.round((b.value / report.totalMb) * 100);
            return (
              <div key={b.label}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="font-medium text-neutral-700">{b.label}</span>
                  <span className="text-neutral-500 dark:text-neutral-400">{fmtMb(b.value)} · {pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: b.color }} />
                </div>
              </div>
            );
          })}
        </div>
      </SettingCard>

      {/* Controls */}
      <SettingCard title="Reduce data" flush>
        <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-neutral-100 dark:border-neutral-800">
          <div>
            <p className="font-semibold text-sm">Data Saver</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">Lower quality media and pause background sync</p>
          </div>
          <Toggle checked={dataSaver} onChange={setDataSaver} label="Data Saver" />
        </div>
        <div className="flex items-center justify-between gap-4 px-5 py-4">
          <div>
            <p className="font-semibold text-sm">Media auto-download</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">When to download photos and video</p>
          </div>
          <Segmented
            value={report.mediaAutoDownload}
            onChange={(mode) => {
              setReport({ ...report, mediaAutoDownload: mode });
              settingsService.setMediaAutoDownload(mode);
            }}
            options={[
              { value: 'always', label: 'Always' },
              { value: 'wifi', label: 'Wi-Fi' },
              { value: 'never', label: 'Never' },
            ]}
          />
        </div>
      </SettingCard>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, tone }: { icon: typeof Wifi; label: string; value: string; tone: string }) {
  const dark = tone.includes('text-white');
  return (
    <div className={`rounded-3xl border border-neutral-200 dark:border-neutral-800 shadow-sm dark:shadow-none p-5 ${tone}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${dark ? 'text-white/70' : 'text-neutral-400 dark:text-neutral-500'}`} />
        <span className={`text-xs font-medium ${dark ? 'text-white/70' : 'text-neutral-500 dark:text-neutral-400'}`}>{label}</span>
      </div>
      <p className="text-2xl font-bold mt-2">{value}</p>
    </div>
  );
}
